/**
 * 3D Planet — staffelknoppen op de productpagina (blocks/tdp-tier-buttons.liquid).
 *
 * Klik op een knop zet de hoeveelheid in het productformulier (quantity-selector van
 * Horizon) en markeert de knop als actief. Typt de klant zelf een aantal, dan volgt de
 * actieve knop mee. Variantprijzen en kortingsgrenzen komen uit Liquid; afronding gebeurt per stuk in centen.
 */

import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';

const QUANTITY_INPUT_SELECTOR = 'quantity-selector-component input[name="quantity"]';

class TdpTierButtons extends HTMLElement {
  connectedCallback() {
    this.root = this.closest('.shopify-section') || this.closest('product-information') || document;
    this.onVariant = (event) => {
      if (event.target.closest('product-card')) return;
      const request = this.request = event.promise;
      request.then(({ detail }) => {
        if (!this.isConnected || request !== this.request) return;
        const updated = detail?.html?.querySelector(`tdp-tier-buttons[data-block-id="${this.dataset.blockId}"]`);
        if (updated) {
          this.dataset.unitCents = updated.dataset.unitCents;
          this.dataset.tiers = updated.dataset.tiers;
          this.querySelector('.tdp-tiers__buttons').replaceWith(updated.querySelector('.tdp-tiers__buttons').cloneNode(true));
        }
        this.syncPressedState();
      }).catch(() => {});
    };
    this.sectionRoot().addEventListener(StandardEvents.productSelect, this.onVariant);
    this.sectionRoot().addEventListener('change', this.onChange = () => this.syncPressedState());
    this.onClick = (event) => this.handleClick(event);
    this.onQuantityEvent = (event) => this.syncPressedState(event);
    this.addEventListener('click', this.onClick);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.onQuantityEvent);
    this.sectionRoot().addEventListener('input', this.onQuantityEvent);
    this.syncPressedState();
  }

  disconnectedCallback() {
    this.sectionRoot().removeEventListener(StandardEvents.productSelect, this.onVariant);
    this.sectionRoot().removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onClick);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.onQuantityEvent);
    this.sectionRoot().removeEventListener('input', this.onQuantityEvent);
  }

  sectionRoot() {
    return this.root;
  }

  /** De hoeveelheid-input van het productformulier in dezelfde sectie. */
  quantityInput() {
    const formId = this.dataset.formId;
    const byForm = formId ? this.sectionRoot().querySelector(`${QUANTITY_INPUT_SELECTOR}[form="${formId}"]`) : null;
    const input = byForm || this.sectionRoot().querySelector(QUANTITY_INPUT_SELECTOR);
    return input instanceof HTMLInputElement ? input : null;
  }

  handleClick(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-tier-qty]') : null;
    if (!button) return;
    const quantity = Number.parseInt(button.getAttribute('data-tier-qty') || '', 10);
    if (!Number.isInteger(quantity) || quantity < 1) return;

    const input = this.quantityInput();
    if (!input) {
      console.warn('[tdp-tier-buttons] Geen hoeveelheid-invoer gevonden in deze sectie');
      return;
    }
    const selector = input.closest('quantity-selector-component');
    const min = Number.parseInt(input.min, 10) || 1;
    const step = Number.parseInt(input.step, 10) || 1;
    const max = selector?.getEffectiveMax?.() ?? (Number.parseInt(input.max, 10) || Infinity);
    const bounded = Math.max(min, Math.min(quantity, max));
    input.value = String(min + Math.floor((bounded - min) / step) * step);
    // Horizon owns the quantity input and publishes the same event as plus/minus.
    if (selector && typeof selector.onQuantityChange === 'function') selector.onQuantityChange();
    if (selector && typeof selector.updateButtonStates === 'function') selector.updateButtonStates();
    input.dispatchEvent(new Event('change', { bubbles: true }));
    this.syncPressedState();
  }

  /** Houdt de actieve knop gelijk aan wat er in het hoeveelheid-veld staat. */
  syncPressedState(event) {
    if (event && event.target instanceof Element && event.type === 'input' && !event.target.matches(QUANTITY_INPUT_SELECTOR)) {
      return;
    }
    const input = this.quantityInput();
    const quantity = input ? Number.parseInt(input.value, 10) : 1;
    this.setPressed(Number.isInteger(quantity) ? quantity : 1);
  }

  setPressed(quantity) {
    for (const button of this.querySelectorAll('[data-tier-qty]')) {
      button.setAttribute('aria-pressed', String(Number(button.getAttribute('data-tier-qty')) === quantity));
    }
    this.renderTotal(quantity);
  }

  /** Regel onder de knoppen: aantal × de ,95-stukprijs en het staffelpercentage; de korting zelf rekent de winkelwagen
   *  (een uitgerekende kortingsprijs als 15,26 oogt onbetrouwbaar, zie blocks/tdp-tier-buttons.liquid). */
  renderTotal(quantity) {
    const check = [...this.sectionRoot().querySelectorAll('tdp-design-check')].find(el => el.dataset.formId === this.dataset.formId);
    const out = this.querySelector('[data-tier-total]') || check?.querySelector('[data-tier-total]');
    if (!out) return;
    if (!Number.isInteger(quantity) || quantity < 1) {
      out.textContent = '';
      return;
    }
    let percent = 0;
    for (const [from, discount] of JSON.parse(this.dataset.tiers || '[]')) {
      if (quantity >= from) percent = discount;
    }
    const fmt = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' });
    const isSet = this.dataset.unit === 'set';
    const unit = isSet ? (quantity === 1 ? 'set' : 'sets') : (quantity === 1 ? 'stuk' : 'stuks');
    const price = fmt.format(Number(this.dataset.unitCents) / 100);
    out.textContent = percent > 0
      ? `${quantity} ${unit} à ${price} · ${percent}% staffelkorting, verrekend in de winkelwagen`
      : `${quantity} ${unit} à ${price} incl. btw`;
    this.dispatchEvent(new CustomEvent('tdp:total-update', { bubbles: true }));
  }
}

if (!customElements.get('tdp-tier-buttons')) {
  customElements.define('tdp-tier-buttons', TdpTierButtons);
}
